import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  Search as SearchSdkType,
  NonNullablePaths,
  SearchSpec,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem from './media-enterprise-public-media-v1-enterprise-item-enterprise-media-items.http.js';
// @ts-ignore
import { transformSDKAudioToRESTAudio } from '@wix/sdk-runtime/transformations/audio';
import { transformRESTAudioToSDKAudio } from '@wix/sdk-runtime/transformations/audio';
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformSDKVideoV2ToRESTVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformSDKDocumentToRESTDocument } from '@wix/sdk-runtime/transformations/document';
import { transformRESTDocumentToSDKDocument } from '@wix/sdk-runtime/transformations/document';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * Duration for video fits better if there will be type specific media item.. however, is it ok to implement
 * an additional one of field called details?
 * A media item in the Enterprise Public Media system. Items can be organized into categories and shared with child accounts by setting their `publishStatus` to `PUBLISHED`.
 */
export interface EnterpriseMediaItem {
  /**
   * Item ID.
   * @maxLength 100
   * @readonly
   */
  _id?: string;
  /**
   * Media type of the item.
   * @readonly
   */
  mediaType?: MediaTypeWithLiterals;
  /**
   * Size of the uploaded file in bytes.
   * @readonly
   * @maxLength 1000
   */
  sizeInBytes?: string | null;
  /**
   * Title of the item. Used for searching and displaying the item.
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * Item assets including URLs for different renditions and formats.
   * @readonly
   */
  assets?: ItemAssets;
  /**
   * User-facing tags describing the item. Indexed for text search; supports partial matching.
   * @maxSize 100
   * @maxLength 100
   */
  displayTags?: string[] | null;
  /**
   * Tags for custom categorization by client applications. Searchable with exact match only.
   * @maxSize 100
   * @maxLength 100
   */
  internalTags?: string[] | null;
  /**
   * Read-only tags generated by the system. Includes information about the uploader (via `uploaderSystemTag`) and external source references (via `uploaderInfoSystemTag`). Use these tags to filter items by their source.
   * @maxSize 100
   * @maxLength 100
   * @readonly
   */
  systemTags?: string[] | null;
  /**
   * IDs of categories the item belongs to. To modify category assignments, call [Link Item To Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/link-item-to-categories), [Unlink Item From Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/unlink-item-from-categories), or [Overwrite Item Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/overwrite-item-categories).
   * @maxSize 100
   * @maxLength 100
   * @readonly
   */
  parentCategoryIds?: string[] | null;
  /** Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard. */
  publishStatus?: PublishStatusWithLiterals;
  /**
   * Date and time the item was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the item was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Internal ID used to reference the item within Wix media systems.
   * @maxLength 100
   * @readonly
   */
  internalId?: string | null;
}

export enum MediaType {
  /** Mixed media types. Used for categories that contain multiple types of media. Individual items should use specific media types (IMAGE, VIDEO, etc.) rather than MIXED. */
  MIXED = 'MIXED',
  /** Image file. */
  IMAGE = 'IMAGE',
  /** Video file. */
  VIDEO = 'VIDEO',
  /** Audio file. */
  AUDIO = 'AUDIO',
  /** Document file. */
  DOCUMENT = 'DOCUMENT',
  /** Vector image file. */
  VECTOR = 'VECTOR',
  /** Archive file. Supported formats: ZIP, RAR, TAR, TAR.GZ, GZ, GZIP, JAR, 7Z, FGZ, and WEBARCHIVE. Maximum file size: 4000MB. */
  ARCHIVE = 'ARCHIVE',
  /** 3D model file. */
  MODEL3D = 'MODEL3D',
}

/** @enumType */
export type MediaTypeWithLiterals =
  | MediaType
  | 'MIXED'
  | 'IMAGE'
  | 'VIDEO'
  | 'AUDIO'
  | 'DOCUMENT'
  | 'VECTOR'
  | 'ARCHIVE'
  | 'MODEL3D';

/** Contains media-specific asset information based on the item's `mediaType`. Only one asset field is populated per item. During initial upload or import, this field may be empty until processing completes. */
export interface ItemAssets extends ItemAssetsAssetsOneOf {
  /** Image assets and metadata. */
  image?: string;
  /** Video assets and metadata. */
  video?: string;
  /** Vector image assets and metadata. */
  vector?: string;
  /** Audio assets and metadata. */
  audio?: string;
  /** Document assets and metadata. */
  document?: string;
  /** Archive file metadata. */
  archive?: Archive;
  /** 3D model metadata. */
  model3d?: Model3D;
}

/** @oneof */
export interface ItemAssetsAssetsOneOf {
  /** Image assets and metadata. */
  image?: string;
  /** Video assets and metadata. */
  video?: string;
  /** Vector image assets and metadata. */
  vector?: string;
  /** Audio assets and metadata. */
  audio?: string;
  /** Document assets and metadata. */
  document?: string;
  /** Archive file metadata. */
  archive?: Archive;
  /** 3D model metadata. */
  model3d?: Model3D;
}

export interface VideoResolution {
  /** Video URL. */
  url?: string;
  /** Video height. */
  height?: number;
  /** Video width. */
  width?: number;
  /**
   * Video format
   * Possible values: ['144p.mp4' '144p.webm' '240p.mp4' '240p.webm' '360p.mp4' '360p.webm' '480p.mp4' '480p.webm'
   * '720p.mp4' '720p.webm' '1080p.mp4' '1080p.webm' ]
   */
  format?: string;
}

export interface Archive {
  /** WixMedia ID. */
  _id?: string;
  /** Archive URL. */
  url?: string;
  /**
   * Archive URL expiration date (when relevant).
   * @readonly
   */
  urlExpirationDate?: Date | null;
  /**
   * Archive size in bytes.
   * @format DECIMAL_VALUE
   */
  sizeInBytes?: string | null;
  /** Archive filename. */
  filename?: string | null;
}

export interface Model3D {
  /** WixMedia 3D ID. */
  _id?: string;
  /** 3D URL. */
  url?: string;
  /** 3D thumbnail Image */
  thumbnail?: string;
  /** 3D alt text. */
  altText?: string | null;
  /**
   * 3D URL expiration date (when relevant).
   * @readonly
   */
  urlExpirationDate?: Date | null;
  /**
   * 3D filename.
   * @readonly
   */
  filename?: string | null;
  /**
   * 3D size in bytes.
   * @readonly
   * @format DECIMAL_VALUE
   */
  sizeInBytes?: string | null;
}

export enum PublishStatus {
  /** Unknown publish status. */
  UNDEFINED = 'UNDEFINED',
  /** Not published. Visible only in the account dashboard. */
  UNPUBLISHED = 'UNPUBLISHED',
  /** Published. Visible in child accounts' media managers. */
  PUBLISHED = 'PUBLISHED',
}

/** @enumType */
export type PublishStatusWithLiterals =
  | PublishStatus
  | 'UNDEFINED'
  | 'UNPUBLISHED'
  | 'PUBLISHED';

/** Triggered when an item's category links change. Also triggers an UPDATED domain event. */
export interface ItemCategoriesChanged {
  /**
   * Current category IDs linked to the item.
   * @maxLength 100
   * @maxSize 100
   */
  itemCategoryIds?: string[] | null;
  /**
   * IDs of categories that were unlinked from the item.
   * @maxLength 100
   * @maxSize 20
   */
  unlinkedCategoryIds?: string[] | null;
  /**
   * IDs of categories that were linked to the item.
   * @maxLength 100
   * @maxSize 20
   */
  linkedCategoryIds?: string[] | null;
  /** Updated item. */
  entity?: EnterpriseMediaItem;
}

export interface ItemUploadCallbackRequest {
  /**
   * ID of the created item.
   * @maxLength 100
   */
  itemId?: string;
  /**
   * Callback token provided to the upload endpoint.
   * @maxLength 10000
   */
  callbackToken?: string;
}

export interface ItemUploadCallbackResponse {
  /** Message describing the callback result. */
  message?: string | null;
}

export interface GenerateFileUploadUrlRequest {
  /**
   * Original file name including the extension. Used as the initial display name for the created item.
   * @maxLength 1000
   */
  fileName?: string;
  /**
   * MIME type of the file. Used to identify the media type.
   * @maxLength 1000
   */
  contentType?: string;
  /** File size in bytes. */
  sizeInBytes?: number;
  /**
   * IDs of additional categories to link the created item to. The item is automatically linked to the root account category and to any categories specified here.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
}

export interface GenerateFileUploadUrlResponse {
  /**
   * Upload URL where the file should be uploaded.
   * @maxLength 10000
   */
  uploadUrl?: string;
}

export interface ImportFileRequest {
  /**
   * URL of the file to import.
   * @format WEB_URL
   */
  url: string;
  /**
   * Original file name including the extension. Used as the initial display name for the created item.
   * @maxLength 1000
   */
  fileName?: string | null;
  /**
   * MIME type of the file. Used to identify the media type.
   * @maxLength 1000
   */
  contentType?: string | null;
  /** File size in bytes. */
  sizeInBytes?: number | null;
  /**
   * IDs of categories to link the created item to. The item will be automatically linked to the root account category.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
  /** Media type of the file. */
  mediaType?: MediaTypeWithLiterals;
  /**
   * Identifier of the external app or system that imported the file. Used to filter items by uploader. For example, if multiple apps upload to the same account, each app can use its own identifier to retrieve only items it created. Stored in `systemTags` prefixed by `_external_uploader:`.
   * @maxLength 100
   */
  uploaderSystemTag?: string | null;
  /**
   * Additional metadata about the external source. Use this to store identifiers of related entities in external systems, such as external item IDs, source URLs, or reference numbers. This complements `uploaderSystemTag`, which identifies the importing app itself. Stored in `systemTags` prefixed by `_external_uploader_info:`.
   * @maxLength 1000
   */
  uploaderInfoSystemTag?: string | null;
}

export interface ImportFileResponse {
  /** Partial item information. At this stage, only `internalId` is populated. All other required fields contain placeholder values. */
  item?: EnterpriseMediaItem;
}

export interface BulkImportFilesRequest {
  /**
   * List of files to import.
   * @minSize 1
   * @maxSize 100
   */
  importFileRequests: ImportFileRequest[];
  /**
   * Whether to include the imported item in the response. Set to `false` to exclude the item from the returned object.
   *
   * Default: `true`
   */
  returnEntity?: boolean | null;
  /**
   * IDs of categories to link all imported items to, in addition to any categories specified in individual `importFileRequests`. Items are also automatically linked to the root account category.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
}

export interface BulkImportFilesResponse {
  /** Results for each individual file import operation. */
  results?: BulkImportFilesResult[];
  /** Metadata about the bulk operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkImportFilesResult {
  /** Metadata about the import operation result. */
  itemMetadata?: ItemMetadata;
  /** Imported item. Returned only if the operation was successful and `returnEntity` is not set to `false`. */
  item?: EnterpriseMediaItem;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface SearchItemsRequest {
  /** Search query for text-based matching across item `title`, `displayTags`, and `internalTags` fields. Partial matching is supported for `title` and `displayTags`. `internalTags` require a full exact match. All filters support equality only. Each query must include a `categoryId` filter. */
  query?: Search;
}

export interface Search extends SearchPagingMethodOneOf {
  /** Pointer to page of results using offset. Can not be used together with 'cursor_paging' */
  paging?: Paging;
  /** A filter object. See documentation [here](https://bo.wix.com/wix-docs/rnd/platformization-guidelines/api-query-language#platformization-guidelines_api-query-language_defining-in-protobuf) */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the form [{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}]
   * @maxSize 50
   */
  sort?: Sorting[];
  /** free text to match in searchable fields */
  search?: SearchDetails;
}

/** @oneof */
export interface SearchPagingMethodOneOf {
  /** Pointer to page of results using offset. Can not be used together with 'cursor_paging' */
  paging?: Paging;
}

export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface SearchDetails {
  /** search term or expression */
  expression?: string | null;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface SearchItemsResponse {
  /**
   * List of items matching the search query.
   * @maxSize 200
   */
  items?: EnterpriseMediaItem[];
  /** Paging information for the search results. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
}

export interface QueryItemsRequest {
  /** Query parameters to filter, sort, and page items. All filters support equality only. Each query must include a `categoryId` filter. Supported sort fields: `createdDate`, `updatedDate`, `title`, `sizeInBytes`. */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
}

export interface QueryItemsResponse {
  /**
   * List of items matching the query.
   * @maxSize 200
   */
  items?: EnterpriseMediaItem[];
  /** Paging information for the query results. */
  pagingMetadata?: PagingMetadataV2;
}

export interface UpdateItemRequest {
  /** Item to update. */
  item: EnterpriseMediaItem;
}

export interface UpdateItemResponse {
  /** Updated item. */
  item?: EnterpriseMediaItem;
}

/** Triggered when an item's `publishStatus` changes. Also triggers an UPDATED domain event. */
export interface PublishStatusChanged {
  /** New publish status of the item. */
  publishStatus?: PublishStatusWithLiterals;
  /** Updated item. */
  entity?: EnterpriseMediaItem;
}

export interface BulkUpdateItemRequest {
  /**
   * List of items to update. Each item must include an ID.
   * @maxSize 20
   * @minSize 1
   */
  updateItemRequests: UpdateItemRequest[];
  /** Whether to return the updated items in the response. */
  returnEntity?: boolean;
}

export interface BulkUpdateItemResponse {
  /** Results for each individual item update operation. */
  results?: BulkItemUpdateResult[];
  /** Metadata about the bulk operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkItemUpdateResult {
  /** Metadata about the update operation result. */
  itemMetadata?: ItemMetadata;
  /** Updated item. Returned only if the operation was successful and `returnEntity` was set to `true`. */
  item?: EnterpriseMediaItem;
}

export interface GetItemRequest {
  /**
   * Item ID.
   * @maxLength 100
   */
  itemId: string;
}

export interface GetItemResponse {
  /** Retrieved item. */
  item?: EnterpriseMediaItem;
}

export interface LinkItemToCategoriesRequest {
  /**
   * Item ID.
   * @maxLength 100
   */
  itemId: string;
  /**
   * IDs of categories to link to the item.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}

export interface LinkItemToCategoriesResponse {
  /**
   * IDs of categories that were successfully linked to the item.
   * @maxLength 100
   * @maxSize 20
   */
  linkedCategoryIds?: string[] | null;
}

export interface UnlinkItemFromCategoriesRequest {
  /**
   * Item ID.
   * @maxLength 100
   */
  itemId: string;
  /**
   * IDs of categories to unlink from the item.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}

export interface UnlinkItemFromCategoriesResponse {
  /**
   * IDs of categories that were successfully unlinked from the item.
   * @maxLength 100
   * @maxSize 20
   */
  unlinkedCategoryIds?: string[] | null;
}

export interface OverwriteItemCategoriesRequest {
  /**
   * Item ID.
   * @maxLength 100
   */
  itemId: string;
  /**
   * IDs of categories the item will be linked to after this operation. All existing category links will be replaced.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}

export interface OverwriteItemCategoriesResponse {
  /**
   * IDs of categories that were linked to the item.
   * @maxLength 100
   * @maxSize 20
   */
  linkedCategoryIds?: string[] | null;
  /**
   * IDs of categories that were unlinked from the item.
   * @maxLength 100
   * @maxSize 20
   */
  unlinkedCategoryIds?: string[] | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface EnterpriseItemCreatedEnvelope {
  entity: EnterpriseMediaItem;
  metadata: EventMetadata;
}

/**
 * Triggered when an item is created.
 * @webhook
 * @eventType wix.media.enterprise_public_media.v1.enterprise_item_created
 * @serviceIdentifier com.wix.media.enterprise_public_media.v1.EnterpriseItemsService
 * @slug created
 * @documentationMaturity preview
 */
export declare function onEnterpriseItemCreated(
  handler: (event: EnterpriseItemCreatedEnvelope) => void | Promise<void>
): void;

export interface EnterpriseItemItemCategoriesChangedEnvelope {
  data: ItemCategoriesChanged;
  metadata: EventMetadata;
}

/**
 * Triggered when an item's category links change.
 * @webhook
 * @eventType wix.media.enterprise_public_media.v1.enterprise_item_item_categories_changed
 * @serviceIdentifier com.wix.media.enterprise_public_media.v1.EnterpriseItemsService
 * @slug item_categories_changed
 * @documentationMaturity preview
 */
export declare function onEnterpriseItemItemCategoriesChanged(
  handler: (
    event: EnterpriseItemItemCategoriesChangedEnvelope
  ) => void | Promise<void>
): void;

export interface EnterpriseItemPublishStatusChangedEnvelope {
  data: PublishStatusChanged;
  metadata: EventMetadata;
}

/**
 * Triggered when an item's `publishStatus` changes.
 * @webhook
 * @eventType wix.media.enterprise_public_media.v1.enterprise_item_publish_status_changed
 * @serviceIdentifier com.wix.media.enterprise_public_media.v1.EnterpriseItemsService
 * @slug publish_status_changed
 * @documentationMaturity preview
 */
export declare function onEnterpriseItemPublishStatusChanged(
  handler: (
    event: EnterpriseItemPublishStatusChangedEnvelope
  ) => void | Promise<void>
): void;

export interface EnterpriseItemUpdatedEnvelope {
  entity: EnterpriseMediaItem;
  metadata: EventMetadata;
}

/**
 * Triggered when an item is updated.
 * @webhook
 * @eventType wix.media.enterprise_public_media.v1.enterprise_item_updated
 * @serviceIdentifier com.wix.media.enterprise_public_media.v1.EnterpriseItemsService
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onEnterpriseItemUpdated(
  handler: (event: EnterpriseItemUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Internal endpoint called by the public media backend when a file is created. Creates domain events for the Enterprise Public Media system.
 * @public
 * @documentationMaturity preview
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.ItemUploadCallback
 */
export async function itemUploadCallback(
  options?: ItemUploadCallbackOptions
): Promise<ItemUploadCallbackResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    itemId: options?.itemId,
    callbackToken: options?.callbackToken,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.itemUploadCallback(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          itemId: '$[0].itemId',
          callbackToken: '$[0].callbackToken',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ItemUploadCallbackOptions {
  /**
   * ID of the created item.
   * @maxLength 100
   */
  itemId?: string;
  /**
   * Callback token provided to the upload endpoint.
   * @maxLength 10000
   */
  callbackToken?: string;
}

/**
 * Generates an upload URL for uploading local files to the Enterprise Public Media system. After uploading the file to the returned URL, the system processes it asynchronously. The item becomes available once processing completes.
 * @public
 * @documentationMaturity preview
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_GENERATE_UPLOAD_URL
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.GenerateFileUploadUrl
 */
export async function generateFileUploadUrl(
  options?: GenerateFileUploadUrlOptions
): Promise<NonNullablePaths<GenerateFileUploadUrlResponse, `uploadUrl`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    fileName: options?.fileName,
    contentType: options?.contentType,
    sizeInBytes: options?.sizeInBytes,
    categoryIds: options?.categoryIds,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.generateFileUploadUrl(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          fileName: '$[0].fileName',
          contentType: '$[0].contentType',
          sizeInBytes: '$[0].sizeInBytes',
          categoryIds: '$[0].categoryIds',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GenerateFileUploadUrlOptions {
  /**
   * Original file name including the extension. Used as the initial display name for the created item.
   * @maxLength 1000
   */
  fileName?: string;
  /**
   * MIME type of the file. Used to identify the media type.
   * @maxLength 1000
   */
  contentType?: string;
  /** File size in bytes. */
  sizeInBytes?: number;
  /**
   * IDs of additional categories to link the created item to. The item is automatically linked to the root account category and to any categories specified here.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
}

/**
 * Imports a file from a URL into the Enterprise Public Media system.
 *
 * To import a single file from your local machine, call [Generate File Upload Url](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/generate-file-upload-url).
 * @param url - URL of the file to import.
 * @public
 * @documentationMaturity preview
 * @requiredField url
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_IMPORT
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.ImportFile
 */
export async function importFile(
  url: string,
  options?: ImportFileOptions
): Promise<
  NonNullablePaths<
    ImportFileResponse,
    | `item._id`
    | `item.mediaType`
    | `item.assets.archive._id`
    | `item.assets.archive.url`
    | `item.assets.model3d._id`
    | `item.assets.model3d.url`
    | `item.displayTags`
    | `item.internalTags`
    | `item.systemTags`
    | `item.parentCategoryIds`
    | `item.publishStatus`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    url: url,
    fileName: options?.fileName,
    contentType: options?.contentType,
    sizeInBytes: options?.sizeInBytes,
    categoryIds: options?.categoryIds,
    mediaType: options?.mediaType,
    uploaderSystemTag: options?.uploaderSystemTag,
    uploaderInfoSystemTag: options?.uploaderInfoSystemTag,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.importFile(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'item.assets.image' },
            { path: 'item.assets.vector' },
            { path: 'item.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'item.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'item.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'item.assets.document' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          url: '$[0]',
          fileName: '$[1].fileName',
          contentType: '$[1].contentType',
          sizeInBytes: '$[1].sizeInBytes',
          categoryIds: '$[1].categoryIds',
          mediaType: '$[1].mediaType',
          uploaderSystemTag: '$[1].uploaderSystemTag',
          uploaderInfoSystemTag: '$[1].uploaderInfoSystemTag',
        },
        singleArgumentUnchanged: false,
      },
      ['url', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ImportFileOptions {
  /**
   * Original file name including the extension. Used as the initial display name for the created item.
   * @maxLength 1000
   */
  fileName?: string | null;
  /**
   * MIME type of the file. Used to identify the media type.
   * @maxLength 1000
   */
  contentType?: string | null;
  /** File size in bytes. */
  sizeInBytes?: number | null;
  /**
   * IDs of categories to link the created item to. The item will be automatically linked to the root account category.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
  /** Media type of the file. */
  mediaType?: MediaTypeWithLiterals;
  /**
   * Identifier of the external app or system that imported the file. Used to filter items by uploader. For example, if multiple apps upload to the same account, each app can use its own identifier to retrieve only items it created. Stored in `systemTags` prefixed by `_external_uploader:`.
   * @maxLength 100
   */
  uploaderSystemTag?: string | null;
  /**
   * Additional metadata about the external source. Use this to store identifiers of related entities in external systems, such as external item IDs, source URLs, or reference numbers. This complements `uploaderSystemTag`, which identifies the importing app itself. Stored in `systemTags` prefixed by `_external_uploader_info:`.
   * @maxLength 1000
   */
  uploaderInfoSystemTag?: string | null;
}

/**
 * Imports multiple files from URLs in a single API call. Check the `itemMetadata` in each result to identify successes and failures, and use `bulkActionMetadata` for an overview of total succeeded and failed operations.
 *
 * To import a single file from a URL, call [Import File](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/import-file).
 * @param importFileRequests - List of files to import.
 * @public
 * @documentationMaturity preview
 * @requiredField importFileRequests
 * @requiredField importFileRequests.url
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_IMPORT
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.BulkImportFiles
 */
export async function bulkImportFiles(
  importFileRequests: NonNullablePaths<ImportFileRequest, `url`, 2>[],
  options?: BulkImportFilesOptions
): Promise<
  NonNullablePaths<
    BulkImportFilesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item._id`
    | `results.${number}.item.mediaType`
    | `results.${number}.item.publishStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    importFileRequests: importFileRequests,
    returnEntity: options?.returnEntity,
    categoryIds: options?.categoryIds,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.bulkImportFiles(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.assets.image' },
            { path: 'results.item.assets.vector' },
            { path: 'results.item.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'results.item.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'results.item.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'results.item.assets.document' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          importFileRequests: '$[0]',
          returnEntity: '$[1].returnEntity',
          categoryIds: '$[1].categoryIds',
        },
        singleArgumentUnchanged: false,
      },
      ['importFileRequests', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkImportFilesOptions {
  /**
   * Whether to include the imported item in the response. Set to `false` to exclude the item from the returned object.
   *
   * Default: `true`
   */
  returnEntity?: boolean | null;
  /**
   * IDs of categories to link all imported items to, in addition to any categories specified in individual `importFileRequests`. Items are also automatically linked to the root account category.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
}

/**
 * Searches for items using text-based search criteria.
 *
 * Search Items runs with these defaults, which you can override:
 *
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * All filters support equality only. Each search must include a `categoryId` filter. Use this endpoint when you need to search for items by text. For retrieving items with custom sorting, use [Query Items](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/query-items).
 *
 * To learn about working with search methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 * @param query - Search query for text-based matching across item `title`, `displayTags`, and `internalTags` fields. Partial matching is supported for `title` and `displayTags`. `internalTags` require a full exact match. All filters support equality only. Each query must include a `categoryId` filter.
 * @public
 * @documentationMaturity preview
 * @requiredField query
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_READ
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.SearchItems
 */
export async function searchItems(
  query: EnterpriseMediaItemSearch
): Promise<
  NonNullablePaths<
    SearchItemsResponse,
    | `items`
    | `items.${number}._id`
    | `items.${number}.mediaType`
    | `items.${number}.assets.archive._id`
    | `items.${number}.assets.archive.url`
    | `items.${number}.assets.model3d._id`
    | `items.${number}.assets.model3d.url`
    | `items.${number}.publishStatus`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.searchItems(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'items.assets.image' },
            { path: 'items.assets.vector' },
            { path: 'items.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'items.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'items.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'items.assets.document' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface EnterpriseMediaItemSearchSpec extends SearchSpec {
  paging: 'offset';
  wql: [
    {
      operators: ['$hasSome'];
      fields: ['parentCategoryIds'];
      sort: 'NONE';
    },
    {
      operators: ['$eq'];
      fields: ['_createdDate', '_updatedDate', 'sizeInBytes', 'title'];
      sort: 'BOTH';
    }
  ];
}

export type CommonSearchWithEntityContext = SearchSdkType<
  EnterpriseMediaItem,
  EnterpriseMediaItemSearchSpec
>;
export type EnterpriseMediaItemSearch = {
  /** 
  Pointer to page of results using offset. Can not be used together with 'cursor_paging'  
  */
  paging?: {
    /** 
  Number of items to load.  
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['paging']>['limit']
      | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonSearchWithEntityContext['paging']>['offset']
      | null;
  };
  /** 
  A filter object. See documentation [here](https://bo.wix.com/wix-docs/rnd/platformization-guidelines/api-query-language#platformization-guidelines_api-query-language_defining-in-protobuf)  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  Sort object in the form [{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}] 
  @maxSize: 50 
  */
  sort?: {
    /** 
  Name of the field to sort by.  
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  free text to match in searchable fields  
  */
  search?: {
    /** 
  search term or expression  
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
  };
};

export const utils = {
  search: {
    ...createSearchUtils<
      EnterpriseMediaItem,
      EnterpriseMediaItemSearchSpec,
      EnterpriseMediaItemSearch
    >(),
  },
  query: {
    ...createQueryUtils<
      EnterpriseMediaItem,
      EnterpriseMediaItemQuerySpec,
      EnterpriseMediaItemQuery
    >(),
  },
};

/**
 * Retrieves a list of up to 200 items, given the provided paging, filtering, and sorting.
 *
 * Query Items runs with these defaults, which you can override:
 *
 * - Sorted by curation order
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * Each query must include a `categoryId` filter. For text-based search, use [Search Items](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/search-items).
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 * @public
 * @documentationMaturity preview
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_READ
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.QueryItems
 */
export function queryItems(): ItemsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    EnterpriseMediaItem,
    'OFFSET',
    QueryItemsRequest,
    QueryItemsResponse
  >({
    func: async (payload: QueryItemsRequest) => {
      const reqOpts =
        ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.queryItems(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryItemsRequest['query']) => {
      const args = [query, {}] as [QueryItemsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryItemsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'items.assets.image' },
              { path: 'items.assets.vector' },
              { path: 'items.assets.model3d.thumbnail' },
            ],
          },
          {
            transformFn: transformRESTVideoV2ToSDKVideoV2,
            paths: [{ path: 'items.assets.video' }],
          },
          {
            transformFn: transformRESTAudioToSDKAudio,
            paths: [{ path: 'items.assets.audio' }],
          },
          {
            transformFn: transformRESTDocumentToSDKDocument,
            paths: [{ path: 'items.assets.document' }],
          },
        ])
      );

      return {
        items: transformedData?.items,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'OFFSET',
    transformationPaths: {},
  });
}

interface QueryOffsetResult {
  currentPage: number | undefined;
  totalPages: number | undefined;
  totalCount: number | undefined;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ItemsQueryResult extends QueryOffsetResult {
  items: EnterpriseMediaItem[];
  query: ItemsQueryBuilder;
  next: () => Promise<ItemsQueryResult>;
  prev: () => Promise<ItemsQueryResult>;
}

export interface ItemsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName: 'sizeInBytes' | 'title' | '_createdDate' | '_updatedDate',
    value: any
  ) => ItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName: 'parentCategoryIds',
    value: any[]
  ) => ItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      'sizeInBytes' | 'title' | '_createdDate' | '_updatedDate'
    >
  ) => ItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      'sizeInBytes' | 'title' | '_createdDate' | '_updatedDate'
    >
  ) => ItemsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => ItemsQueryBuilder;
  /** @param skip - Number of items to skip in the query results before returning the results.
   * @documentationMaturity preview
   */
  skip: (skip: number) => ItemsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<ItemsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.QueryItems
 * @requiredField query
 */
export async function typedQueryItems(
  query: EnterpriseMediaItemQuery
): Promise<
  NonNullablePaths<
    QueryItemsResponse,
    | `items`
    | `items.${number}._id`
    | `items.${number}.mediaType`
    | `items.${number}.assets.archive._id`
    | `items.${number}.assets.archive.url`
    | `items.${number}.assets.model3d._id`
    | `items.${number}.assets.model3d.url`
    | `items.${number}.publishStatus`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.queryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'items.assets.image' },
            { path: 'items.assets.vector' },
            { path: 'items.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'items.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'items.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'items.assets.document' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface EnterpriseMediaItemQuerySpec extends QuerySpec {
  paging: 'offset';
  wql: [
    {
      fields: ['parentCategoryIds'];
      operators: ['$hasSome'];
      sort: 'NONE';
    },
    {
      fields: ['_createdDate', '_updatedDate', 'sizeInBytes', 'title'];
      operators: ['$eq'];
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  EnterpriseMediaItem,
  EnterpriseMediaItemQuerySpec
>;
export type EnterpriseMediaItemQuery = {
  /** 
  Paging options to limit and skip the number of items.  
  */
  paging?: {
    /** 
  Number of items to load.  
  */
    limit?: NonNullable<CommonQueryWithEntityContext['paging']>['limit'] | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonQueryWithEntityContext['paging']>['offset']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by.  
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

/**
 * Updates an item.
 * To update multiple items in a single API call, call [Bulk Update Item](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/bulk-update-item).
 * @param _id - Item ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField item
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_UPDATE
 * @applicableIdentity APP
 * @returns Updated item.
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.UpdateItem
 */
export async function updateItem(
  _id: string,
  item: UpdateItem
): Promise<
  NonNullablePaths<
    EnterpriseMediaItem,
    | `_id`
    | `mediaType`
    | `assets.archive._id`
    | `assets.archive.url`
    | `assets.model3d._id`
    | `assets.model3d.url`
    | `displayTags`
    | `internalTags`
    | `systemTags`
    | `parentCategoryIds`
    | `publishStatus`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ item: { ...item, id: _id } }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'item.assets.image' },
          { path: 'item.assets.vector' },
          { path: 'item.assets.model3d.thumbnail' },
        ],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'item.assets.video' }],
      },
      {
        transformFn: transformSDKAudioToRESTAudio,
        paths: [{ path: 'item.assets.audio' }],
      },
      {
        transformFn: transformSDKDocumentToRESTDocument,
        paths: [{ path: 'item.assets.document' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.updateItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'item.assets.image' },
            { path: 'item.assets.vector' },
            { path: 'item.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'item.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'item.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'item.assets.document' }],
        },
      ])
    )?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { item: '$[1]' },
        explicitPathsToArguments: { 'item.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'item']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateItem {
  /**
   * Item ID.
   * @maxLength 100
   * @readonly
   */
  _id?: string;
  /**
   * Media type of the item.
   * @readonly
   */
  mediaType?: MediaTypeWithLiterals;
  /**
   * Size of the uploaded file in bytes.
   * @readonly
   * @maxLength 1000
   */
  sizeInBytes?: string | null;
  /**
   * Title of the item. Used for searching and displaying the item.
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * Item assets including URLs for different renditions and formats.
   * @readonly
   */
  assets?: ItemAssets;
  /**
   * User-facing tags describing the item. Indexed for text search; supports partial matching.
   * @maxSize 100
   * @maxLength 100
   */
  displayTags?: string[] | null;
  /**
   * Tags for custom categorization by client applications. Searchable with exact match only.
   * @maxSize 100
   * @maxLength 100
   */
  internalTags?: string[] | null;
  /**
   * Read-only tags generated by the system. Includes information about the uploader (via `uploaderSystemTag`) and external source references (via `uploaderInfoSystemTag`). Use these tags to filter items by their source.
   * @maxSize 100
   * @maxLength 100
   * @readonly
   */
  systemTags?: string[] | null;
  /**
   * IDs of categories the item belongs to. To modify category assignments, call [Link Item To Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/link-item-to-categories), [Unlink Item From Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/unlink-item-from-categories), or [Overwrite Item Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/overwrite-item-categories).
   * @maxSize 100
   * @maxLength 100
   * @readonly
   */
  parentCategoryIds?: string[] | null;
  /** Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard. */
  publishStatus?: PublishStatusWithLiterals;
  /**
   * Date and time the item was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the item was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Internal ID used to reference the item within Wix media systems.
   * @maxLength 100
   * @readonly
   */
  internalId?: string | null;
}

/**
 * Updates multiple items in a single API call.
 *
 * To update a single item, call [Update Item](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/update-item).
 * @param updateItemRequests - List of items to update. Each item must include an ID.
 * @public
 * @documentationMaturity preview
 * @requiredField updateItemRequests
 * @requiredField updateItemRequests.item._id
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.BulkUpdateItem
 */
export async function bulkUpdateItem(
  updateItemRequests: NonNullablePaths<UpdateItemRequest, `item._id`, 3>[],
  options?: BulkUpdateItemOptions
): Promise<
  NonNullablePaths<
    BulkUpdateItemResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item._id`
    | `results.${number}.item.mediaType`
    | `results.${number}.item.publishStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      updateItemRequests: updateItemRequests,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'updateItemRequests.item.assets.image' },
          { path: 'updateItemRequests.item.assets.vector' },
          { path: 'updateItemRequests.item.assets.model3d.thumbnail' },
        ],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [{ path: 'updateItemRequests.item.assets.video' }],
      },
      {
        transformFn: transformSDKAudioToRESTAudio,
        paths: [{ path: 'updateItemRequests.item.assets.audio' }],
      },
      {
        transformFn: transformSDKDocumentToRESTDocument,
        paths: [{ path: 'updateItemRequests.item.assets.document' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.bulkUpdateItem(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.assets.image' },
            { path: 'results.item.assets.vector' },
            { path: 'results.item.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'results.item.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'results.item.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'results.item.assets.document' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          updateItemRequests: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['updateItemRequests', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateItemOptions {
  /** Whether to return the updated items in the response. */
  returnEntity?: boolean;
}

/**
 * Retrieves an item by ID.
 * @param itemId - Item ID.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_READ
 * @applicableIdentity APP
 * @returns Retrieved item.
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.GetItem
 */
export async function getItem(
  itemId: string
): Promise<
  NonNullablePaths<
    EnterpriseMediaItem,
    | `_id`
    | `mediaType`
    | `assets.archive._id`
    | `assets.archive.url`
    | `assets.model3d._id`
    | `assets.model3d.url`
    | `displayTags`
    | `internalTags`
    | `systemTags`
    | `parentCategoryIds`
    | `publishStatus`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ itemId: itemId });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.getItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'item.assets.image' },
            { path: 'item.assets.vector' },
            { path: 'item.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'item.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'item.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'item.assets.document' }],
        },
      ])
    )?.item!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { itemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['itemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Links an item to multiple categories. Adds to existing category links without removing them.
 *
 * To replace all category links, call [Overwrite Item Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/overwrite-item-categories). To remove specific category links, call [Unlink Item From Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/unlink-item-from-categories).
 * @param itemId - Item ID.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_UPDATE_CATEGORIES
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.LinkItemToCategories
 */
export async function linkItemToCategories(
  itemId: string,
  options?: LinkItemToCategoriesOptions
): Promise<
  NonNullablePaths<LinkItemToCategoriesResponse, `linkedCategoryIds`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    itemId: itemId,
    categoryIds: options?.categoryIds,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.linkItemToCategories(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          itemId: '$[0]',
          categoryIds: '$[1].categoryIds',
        },
        singleArgumentUnchanged: false,
      },
      ['itemId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface LinkItemToCategoriesOptions {
  /**
   * IDs of categories to link to the item.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}

/**
 * Unlinks an item from multiple categories. Removes specified category links while keeping others.
 *
 * To replace all category links, call [Overwrite Item Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/overwrite-item-categories). To add category links, call [Link Item To Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/link-item-to-categories).
 * @param itemId - Item ID.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_UPDATE_CATEGORIES
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.UnlinkItemFromCategories
 */
export async function unlinkItemFromCategories(
  itemId: string,
  options?: UnlinkItemFromCategoriesOptions
): Promise<
  NonNullablePaths<UnlinkItemFromCategoriesResponse, `unlinkedCategoryIds`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    itemId: itemId,
    categoryIds: options?.categoryIds,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.unlinkItemFromCategories(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          itemId: '$[0]',
          categoryIds: '$[1].categoryIds',
        },
        singleArgumentUnchanged: false,
      },
      ['itemId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UnlinkItemFromCategoriesOptions {
  /**
   * IDs of categories to unlink from the item.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}

/**
 * Replaces all existing category links with the specified categories.
 *
 * To add category links without removing existing ones, call [Link Item To Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/link-item-to-categories). To remove specific category links, call [Unlink Item From Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/unlink-item-from-categories).
 * @param itemId - Item ID.
 * @public
 * @documentationMaturity preview
 * @requiredField itemId
 * @permissionId MEDIA.ENTERPRISE_MEDIA_ITEMS_UPDATE_CATEGORIES
 * @applicableIdentity APP
 * @fqn com.wix.media.enterprise_public_media.v1.EnterpriseItemsService.OverwriteItemCategories
 */
export async function overwriteItemCategories(
  itemId: string,
  options?: OverwriteItemCategoriesOptions
): Promise<
  NonNullablePaths<
    OverwriteItemCategoriesResponse,
    `linkedCategoryIds` | `unlinkedCategoryIds`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    itemId: itemId,
    categoryIds: options?.categoryIds,
  });

  const reqOpts =
    ambassadorWixMediaEnterprisePublicMediaV1EnterpriseItem.overwriteItemCategories(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          itemId: '$[0]',
          categoryIds: '$[1].categoryIds',
        },
        singleArgumentUnchanged: false,
      },
      ['itemId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface OverwriteItemCategoriesOptions {
  /**
   * IDs of categories the item will be linked to after this operation. All existing category links will be replaced.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}
